import "./App.css";
import Footer from "./functions/Footer.js";
import ProjHead from "./functions/ProjHead.js";
import React from "react";
import Bottom from "./functions/Bottom.js";
import Scrollspy from "react-scrollspy";
import { HashLink } from "react-router-hash-link";
import Zoom from "./functions/Zoom.js";

function Buzzfeed(props) {
  return (
    <div>
      <div className="bodytext">
        <ProjHead
          title="BuzzFeed Pubhub Automations Editor"
          description="Empowering users to edit and optimize social media automations"
          image="./buzzfeed/buzzfeedcover.png"
          timeline=" Summer 2021, 8 weeks as only product design intern"
          role="With"
          team="1 Design lead, 1 product specialist, 1 engineering lead, engineers"
          tools="Figma"
          context="Internship Project - Focus on product thinking and interaction"
        />
        <div className="sticky">
          <nav className="section-nav">
            <Scrollspy
              items={[
                "problem",
                "solution",
                "research",
                "editing",
                "metrics",
                "conclusion",
              ]}
              currentClassName="is-current"
            >
              <li>
                <HashLink to="#problem">Problem</HashLink>
              </li>
              <li>
                <HashLink to="#solution">Solution Overview</HashLink>
              </li>
              <li>
                <HashLink to="#research">Research</HashLink>
              </li>
              <li>
                <HashLink to="#editing">Prototyping Editing</HashLink>
              </li>
              <li>
                <HashLink to="#metrics">Prototyping Metrics</HashLink>
              </li>
              <li>
                <HashLink to="#conclusion">Conclusion</HashLink>
              </li>
            </Scrollspy>
          </nav>
        </div>
        <div className="projmargins">
          <h2 id="Overview">Overview</h2>
          <p>
            Currently, a majority of content on BuzzFeed socials is
            auto-generated. A bulk of social media posted content is actually
            older BuzzFeed articles that have done well in the past. Articles
            are auto-posted or recommended to social media strategists to be
            posted through a platform called <strong>Pubhub automations</strong>
            . Although this saves a lot of time, it takes a decent effort to
            optimize automations because users{" "}
            <strong>(social media strategists)</strong>, don't have any editing
            privileges.
          </p>
          <h2 id="problem">Problem</h2>
          <p>
            Currently to edit an automation users have to talk to{" "}
            <strong>the product specialist</strong>. This causes several
            problems:
          </p>
          <div className="three">
            <p>
              <span>⏰</span>
              <br /> Lost time spent on consultation and manual changes on the
              backend could be used for other purposes
            </p>
            <p>
              <span>❌</span>
              <br />
              It disincentivizes users to experiment with their social
              automation strategy as they feel like they&#39;re bothering the
              product specialist with the slightest changes
            </p>
            <p>
              <span>🤔</span> <br />
              It decreases autonomy for users to learn more about how they can
              optimize automation parameters for themselves{" "}
            </p>
          </div>
          <p>
            <Zoom src="buzzfeed/Untitled.png" alt="the current problem" />
          </p>
          <h2 id="solution">Solution Overview</h2>
          <h3>Main High-Fidelity Editing Workflow</h3>
          <p>
            For the product solution to be shipped I settled on allowing an
            editing workflow for users to make changes to these parameters.{" "}
          </p>
          <p>
            <Zoom src="buzzfeed/buzzfeed2.gif" alt="finished_flow.gif" />
          </p>
          <h3>Metric Integration Proposals</h3>
          <p>
            I also investigated how to encourage use of data to drive decision
            making - I prototyped three main{" "}
            <strong>high feasibility + high impact </strong> proposals which
            were met positively by users.
          </p>
          <Zoom src="buzzfeed/Untitled%201.png" alt="metrics" />
          <p>As a result, the new user flow would look like the following.</p>
          <Zoom src="buzzfeed/proposed.png" alt="new user flow" />

          <h2 id="research">Research</h2>
          <p>
            However to get to the final product, I had to understand the fine
            details of what users were currently doing. During user research
            calls with ten social publishers of varying rank - I asked:
          </p>
          <ul>
            <li>
              How do users make these decisions to edit parameters? Are there
              ways we could provide information to help them?
            </li>
            <li>
              What is the current state of education with automations within the
              social publishers?
            </li>
            <li>
              What action flows (editing, creating new automations) do users do
              the most?
            </li>
          </ul>
          <h3>Key Takeaways</h3>
          <div className="three">
            <p>
              <span>🗒️</span>
              <br /> Got a list of parameters and important metrics to help
              introduce a <strong>scalability plan</strong>
            </p>
            <p>
              <span>❓</span> <br />
              Users are not intimidated by automation technicalities but rather{" "}
              <strong>
                they're unsure how different parameters affected results
              </strong>{" "}
            </p>
            <p>
              <span>1️⃣</span>
              <br /> Users edit more than create, they will focus on only
              editing <strong>one automation at a time</strong>{" "}
            </p>
          </div>

          <div className="underline">
            <h2 id="editing">
              <em>Part 1: Prototyping Editing Interface 📝</em>
            </h2>
          </div>
          <p>
            While prototyping there was{" "}
            <strong>no pre-defined design system</strong>, however I looked
            through previous design files and made sure to keep consistency in{" "}
            <strong>
              type, buttons, toggles, symbols, form components and tabs
            </strong>{" "}
            by utilizing components and style Figma tools.{" "}
          </p>
          <h2 id="details-view">Details View Overview</h2>
          <p>
            When figuring out the entrypoint I decided to go with option 2
            because it was the most clear that by pressing the button the user
            would both edit and view at the same time.
          </p>
          <p>
            <Zoom src="buzzfeed/entrypoint.png" alt="entrypoint iterations" />
          </p>
          <p>
            For the general flow I had settled on having the details view show
            up through a <strong>popup</strong> do reduce cognitive friction of
            opening a new page and allow users to do their day to day of quickly
            checking on different automations.
          </p>
          <p>
            <Zoom src="buzzfeed/Untitled%2013.png" alt="overall flow" />
          </p>
          <h2 id="how-do-we-add-and-remove-conditions-">
            How do we add and remove conditions?
          </h2>
          <h3 id="adding">Adding</h3>
          <Zoom src="buzzfeed/Untitled%2014.png" alt="adding iterations" />
          <p>
            I went with option 3 because we didn't want information overload on
            the users by showing all the possible conditions they could activate
            when they only focused on one or two at a time. Because users tend
            to edit more than add new conditions it was better to have them
            focus on the edit action.
          </p>
          <h3 id="editing-adding-in-a-block-format">
            Editing/adding in a Block Format
          </h3>
          <p>
            I explored several directions in how to edit/add in this new block
            format.
          </p>
          <div className="row">
            <div className="col">
              <p>
                <Zoom src="buzzfeed/Untitled%2015.png" alt="add and edit" />
              </p>
            </div>
            <div className="col">
              <p>
                Initially, I had two options that involved{" "}
                <strong>multiple pages</strong>. To edit or add, it would take
                the user to a new page/popup within a popup. However there
                wasn't enough supplemental information needed to warrant the
                tradeoff of increased friction (clicks) in task completion.
              </p>
            </div>
          </div>
          <p>
            I then brainstormed <strong>two single page options</strong>.{" "}
          </p>
          <p>
            <Zoom src="buzzfeed/Untitled%2016.png" alt="inline editing" />
          </p>
          <p>
            In the end I went with inline editing and adding because there
            wasn't any supplemental detail for each condition to justify a
            focused-in sidebar view.
          </p>
          {/* <h3 id="adding-inline">Adding Inline</h3>
          <p>
            I conducted more explorations in adding inline. Originally the
            design problem was that out of the three subsections (Time,
            Relevancy Score, and Conditions), only <strong>Conditions</strong>{" "}
            would have addable parts.
          </p>
          <p>
            Our two constraints with adding was that it{" "}
            <strong>had to be scalable</strong> and allow users to intuitively{" "}
            <strong>understand.</strong>
          </p>
          <p>
            <img src="buzzfeed/Untitled%2017.png" alt="Untitled" />
          </p>
          <p>
            However the feedback I got with option 1 was that it was unclear
            what would be added, however with option 2 - it seemed more clear
            that it was related.
          </p>
          <p>
            A decent amount of space was being taken up by{" "}
            <strong>explanatory text</strong>, I determined that onboarding
            shouldn&#39;t be solved solely by UI, because most conditions were
            explainable enough simply by the name. By taking out the explanatory
            text, we settled on the below iteration to make it as simple as
            possible.
          </p>
          <p>
            <img src="buzzfeed/Untitled%2018.png" alt="Untitled" />
          </p>
          <p>
            <strong>Dealing with Changes in Scope:</strong> As the project
            progressed I learned tha
            <strong>
              t all conditions were add/removable except for rerun interval
            </strong>
            . However because I was focused on making it scalable I was able to
            easily extend it to others.
          </p> */}
          <h3 id="content-design-making-a-human-readable-format">
            Content Design: Making a Human Readable Format
          </h3>
          <p>
            There were two key edits involved to mitigate the need for
            explanatory supplemental text. One was{" "}
            <strong>breaking it up</strong> into three subsections that also had
            titled explaining the functions of each. The other was{" "}
            <strong>changing technical terms</strong> like &quot;active
            hours&quot; to more understandable ones like &quot;post
            between&quot;.
          </p>
          <p>
            <Zoom
              src="buzzfeed/Untitled%2019.png"
              alt="removal/inline removal"
            />
          </p>
          <h3 id="removing">Removing</h3>
          <p>
            With removal I went through a similar process, ultimately, we went
            with inline removal as it reinforced each condition as a separate
            movable entity.
          </p>
          <p>
            <Zoom src="buzzfeed/Untitled%2020.png" alt="removal iterations" />
          </p>
          <h2 id="defining-the-layout-of-the-page">
            Defining the Layout of the Page
          </h2>
          <div class="row">
            <div class="col">
              <h3 id="f-shape-pattern">F-Shape Pattern</h3>
              <p>
                The F-shape pattern indicates that people read horizontally
                downards. I experimented with having a layout in this approach
                below where the two most editable conditions (content relevancy
                and posting schedule) were next to each other before the user
                goes onto the next row.
              </p>
            </div>
            <div class="col">
              <Zoom src="buzzfeed/Untitled%2021.png" alt="f-shape" />
            </div>
          </div>
          <p>
            However, this led to the problem -{" "}
            <strong>how would it scale?</strong> With the two column approach
            this lead to the possibility of many awkward white spaces and
            unbalanced grids should there be missing parameters.
          </p>
          <h3 id="separating-the-subcategories">
            Separating the Subcategories
          </h3>
          <p>
            I then experimented with a layout that gave each subcategory equal
            weight.{" "}
          </p>
          <Zoom src="buzzfeed/Untitled%2022.png" alt="analysis" />
          <p>
            Option 1 introduced scalability issue as conditions added would make
            it more unbalanced. I leaned towards vertical scanning - having
            every section layer on top of each other, and for conditions to fill
            up the space horizontally. This fell more in line with{" "}
            <strong>layer cake scanning</strong> - where the desired pattern
            would be to look at the subheadings before diving into the one
            they’re interested and scanning the details.
          </p>
          <p>
            Visually option 3 was better than option 2 as it was easier to
            distinguish the different blocks.
          </p>
          <div className="row">
            <div className="col">
              <p>
                <Zoom
                  src="buzzfeed/Untitled%2023.png"
                  alt="visual iterations"
                />
              </p>
            </div>
            <div className="col">
              <p>
                However, the giant blocks made it unclear that each condition
                was individually removable (not linked to each other). I was
                inspired by patterns in tools such as Scratch to break it up.{" "}
              </p>
            </div>
          </div>
          <p>
            <Zoom src="buzzfeed/Untitled%2024.png" alt="visual iterations" />
          </p>
          <p>
            I maintained a gray background to again make each block more
            distinguishable from each other and the background.
          </p>
          <h2 id="save">Finishing Questions on Editing and Saving </h2>
          <p>
            After finalizing on the visual layout I needed to refine the edge
            cases of editing and saving.
          </p>
          <h3>Global Edit Button</h3>
          <p>
            <Zoom src="buzzfeed/Untitled%2025.png" alt="global edit button" />
          </p>
          <p>
            For editing I was between several concepts that involved having a
            locked preview. However because users edited one metric at a time,
            the probability of an error to warrant an extra layer of protection
            was low. Ultimately I went with option 4 - also because the
            entrypoint was an edit button, the user would understand they were
            launching into an edit state.
          </p>
          <h3 id="Saving">Saving</h3>
          <p>
            I was unsure whether to allow used to save on empty or when filled.
            Only allowing save on filled blocks would set a guardrail to make
            sure all blocks on the page were relevant to the user.
          </p>
          <p>
            <Zoom src="buzzfeed/Untitled%2028.png" alt="save on empty" />
          </p>
          <p>
            However, saving on empty would allow for scaffolding as users
            required back and forth interaction to settle on how they were
            editing automations for testing purposes. Because there wasn't clear
            value to ensuring only save on fill and it took more engineering
            lift, we went with option 1.
          </p>
          <h3 id="user-testing-feedback">User Testing + Feedback</h3>
          <p>
            After 5 rounds of user testing, I found most tasks were completed.
            The only struggle was that users would find the "Add" button but not
            understand what it meant. Once users interacted with the dropdown
            they were able to accomplish the task successfully. As a result, I
            changed the language of the add entrypoint from &quot;Add&quot; to
            &quot;Add Condition&quot;. Although a small fix, I felt like it was
            the one that maximized feasibility and effectiveness.
          </p>
          <p>
            Not only was the prototype, but it also had{" "}
            <strong>strong product value</strong>. Below is a quote from the{" "}
            <strong>head of social strategy</strong>, a stakeholder that manages
            all the social media strategists (user of the tool) and sets the
            tone for the team.
          </p>
          <h3>
            <em>
              “"I always don’t want to bug Mireille (Pubhub's Product
              Specialist) to change one small thing, and that was a hurdle so
              being able to edit is a big deal.”{" "}
            </em>{" "}
            - Cathy Ngo, Head of Social Strategy
          </h3>
          <div className="underline">
            <h2 id="metrics">
              <em>Part 2: Prototyping Data Metrics 📈</em>
            </h2>
          </div>
          <p>
            A major problem that we ran into was that users{" "}
            <strong>did not know when or what to edit.</strong> After
            brainstorming, I realized that optimizing consisted of two parts:
          </p>
          <ul>
            <li>
              <strong>Looking in the past:</strong> To fine-tune automations,
              users would have to look at past data to refine what previously
              worked, or to revert to other versions.
            </li>
            <li>
              <strong>Noticing differences:</strong> What is the current state
              of education with automations within the social publishers?
            </li>
          </ul>
          <h2 id="metrics">Looking into the Past</h2>
          <h3>Line Graph</h3>
          <p>
            In the beginning, I thought a line graph would be perfect for
            determining the success of automations in its trajectory throughout
            time.{" "}
          </p>
          <p>
            <Zoom src="buzzfeed/Untitled%203.png" alt="looking into the past" />
          </p>
          <p>
            However, I realized that because users would be editing multiple
            parameters, it made less sense to include graphs because it would be
            harder to pinpoint what changes in metrics caused the difference.
          </p>
          <h3>Past History</h3>
          <p>
            Eventually, this evolved into the past history view - I debated it
            having a dropdown on the same page versus on a separate tab. I went
            with a separate tab as I scoped out user behavior - users would
            ultimately only edit one parameter at a time so an avenue for
            multitasking wouldn&#39;t be needed.
          </p>
          <p>
            <Zoom src="buzzfeed/Untitled%204.png" alt="past history view" />
          </p>
          <p>
            Based off the explorations I decided to only show parameters that
            were editable in the preview and allow users the option to view all
            parameters if needed. One aspect I was unsure about was the utility
            of <strong>having users toggle what was reviewable.</strong> To get
            more feedback, I went to user testing.
          </p>
          <p>
            <Zoom src="buzzfeed/Untitled%206.png" alt="User Tested Prototype" />
          </p>

          <p>
            After segmenting it a bit more, I was able to land on the final
            version below which broke it up into three subsections and
            emphasized the different settings so users could easily understand
            the specifics.
          </p>
          <p>
            <Zoom src="buzzfeed/Untitled%208.png" alt="final version" />
          </p>
          <h2 id="metrics">Noticing Differences</h2>
          <h3>Looker Integration</h3>
          <p>
            From user research I also learned that there were Looker dashboards
            that people used - however many were unaware they existed. I
            brainstormed two options to have it as an outlink or to have it as a
            separate page with graphs.
          </p>
          <p>
            <Zoom src="buzzfeed/Untitled%209.png" alt="looker integration" />
          </p>
          <p>
            Although the outlink would be low-lift, it would be hard to ensure
            that users would be inclined to click on it. To ensure high
            visibility, we integrated the graphs into the UI itself so users
            wouldn&#39;t have to open up another tab.
          </p>
          <h3>Metrics</h3>
          <p>
            However, the difficulty with the Looker is that it was hard to tell
            the numerical changes for each automation.{" "}
          </p>
          <p>
            <Zoom src="buzzfeed/Untitled%2010.png" alt="Untitled" />
          </p>
          <p>
            After talking with engineers and presenting different statistical
            options, I learned that the only feasible ones would be the{" "}
            <strong>volume published</strong> (for publishing) and also the{" "}
            <strong>number used out of total recommended</strong> (for
            recommendations). I also decided that the &quot;percent
            changes&quot;, although a valid indicator of a positive or negative
            change - <em>would need more data and testing</em> to indicate
            whether or not an actual investigable change had occurred or if it
            was just within the flux of current events.
          </p>
          <h3>User Reactions from User Testing</h3>
          <p>
            When testing this feature - we found that 5/5 users had positive
            responses to all three options - in fact, many expressed they would
            use each metric for difference purposes.
          </p>

          <h3>
            <em>
              “The metrics preview is good to keep an eye on pages that are 100%
              automated and quick changes. With the graphs, I want my team to
              keep an eye on pages we aren’t actively scheduling to. Finally, I
              like the past history because it makes retros easier.”{" "}
            </em>{" "}
            - Cathy Ngo, Head of Social Strategy
          </h3>
          <h2 id="solution">Solution Overview</h2>
          <p>
            We ended up with positive feedback not only from the social media
            publishers on Buzzfeed, but also the Director of Social Media
            Strategy herself approved of the designs and agreed with the
            value-add of the product.
          </p>
          <img src="buzzfeed/buzzfeed2.gif" alt="finished_flow.gif"></img>
          <Zoom src="buzzfeed/Untitled%201.png" alt="Untitled" />
          <h3>Edge Cases</h3>
          <p>
            I also collaborated with product and engineering to understand edge
            cases and prepare files for handoff.
          </p>

          <Zoom src="buzzfeed/edge.jpg" alt="edge states" />
          <h3>Measuring Success</h3>
          <p>
            I would measure the success of this feature by qualitatively noting
            any changes in strategists' plans and observing the frequency and
            average time spent per day on the editing pages as well as the
            metric pages.
          </p>
          <h2 id="conclusion">Takeaways</h2>
          <ul>
            <li>
              <strong>Learning content design</strong> - a large part of this
              internship was having a clear copy that contained all the
              necessary parameters and cut out unnecessary jargon.
            </li>
            <li>
              <strong>Validate with limited testing</strong> - I learned how to
              formulate hypothesis and different decision frameworks to predict
              which designs would work best for users.
            </li>
          </ul>
          <div className="row">
            <div className="col">
              <p>
                <Zoom src="buzzfeed/announcements.png" alt="announcements" />
              </p>
            </div>
            <div className="col">
              <p>
                <strong>Exercise product-thinking early</strong> - My first
                project was a notifications project, however I was able to
                quickly scope out the project and determine for the engineering
                capacity given, it would be better to use a free out-of-box
                alternative.
              </p>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <p>
                <strong>Finer Visual Details </strong> - I deep-dived into
                autolayout to ensure visual consistency in the module and to
                make sure aspects such as spacing and margins would be clear to
                engineering.
              </p>
            </div>
            <div className="col">
              <p>
                <Zoom src="buzzfeed/autolayout.png" alt="autolayout" />
              </p>
            </div>
          </div>
          <h2 id="extra">Extra</h2>
          <p>
            <img src="buzzfeed/Untitled%2029.png" alt="Untitled" />
          </p>
          <p>
            Besides my main internship I was able to accomplish two side
            projects - a Hackweek project involving matching fits for BuzzFeed
            Commerce that evolved my visual design skills and contributing to
            auditing the typography of BuzzFeed products for the design system
            team.
          </p>
          <div className="row">
            <img src="./buzzfeed/combine.png" />
          </div>
        </div>
        <Bottom nextproject="Twitter Case Study" next="/twitter" />
      </div>

      <Footer />
    </div>
  );
}
export default Buzzfeed;
